/**
 * The host's "guest is ready" popup (spec §8).
 *
 * On CommunicatorGuestReady a sticky toast "Bo (gæst) er klar til videoopkald" appears with "Ring op" and "Senere", and
 * an OS notification whose click brings the app forward. A CommunicatorGuestLeft withdraws that toast and its
 * notification and shows "Bo har forladt siden" instead, so "Ring op" can never dial a guest who has gone.
 *
 * The glue to PrimeVue's toast, DesktopNotification, Electron and the call is injected through deps, so this is tested
 * with fakes.
 *
 * A popup is dropped when its invite's expiresAt passes ("Ring op" would only be refused) and all popups are dropped on
 * logout, so the next user of the app never sees them.
 */

import globalEvents from '@/utilities/GlobalEvents'

/** setTimeout cannot wait longer than this (a signed 32-bit number of milliseconds) - an invite may live 30 days */
const MAX_TIMER_MS = (2 ** 31) - 1

export interface GuestReadyToastsDeps {
	/** Show the sticky "guest is ready" toast; onCall is "Ring op" */
	addReadyToast: (inviteId: string, guestName: string, onCall: () => void) => void
	/** Show the "guest has left" toast */
	addLeftToast: (guestName: string) => void
	/** Remove the ready toast for an invite */
	removeToast: (inviteId: string) => void
	/** Show an OS notification; returns a handle that can be closed */
	notify: (inviteId: string, guestName: string, onClick: () => void) => { close: () => void }
	/** Bring the app window to the front (Electron) and focus it */
	focusApp: () => void
	/** "Ring op" was chosen for an invite */
	onRingUp: (inviteId: string, guestName: string) => void
}

/**
 * Wire the guest-ready popup to the global events. Returns a teardown that removes the listeners.
 * @param deps The UI glue
 */
export function createGuestReadyToasts(deps: GuestReadyToastsDeps): { stop: () => void } {
	const notifications = new Map<string, { close: () => void }>()

	const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>()

	function clear(inviteId: string) {
		clearTimeout(expiryTimers.get(inviteId))
		expiryTimers.delete(inviteId)
		deps.removeToast(inviteId)
		notifications.get(inviteId)?.close()
		notifications.delete(inviteId)
	}

	/** Drop the popup when the invite expires - waiting in steps when that is further off than a timer can wait */
	function dropAtExpiry(inviteId: string, expiresAtMs: number) {
		const wait = expiresAtMs - Date.now()

		expiryTimers.set(inviteId, setTimeout(() => {
			if (expiresAtMs - Date.now() > 0) { dropAtExpiry(inviteId, expiresAtMs) } else { clear(inviteId) }
		}, Math.min(Math.max(wait, 0), MAX_TIMER_MS)))
	}

	const onReady = (data: { inviteId: string, guestName: string, expiresAt: string }) => {
		// A guest who re-joins (a suspended tab) is announced again: replace the old popup, never stack a second one
		clear(data.inviteId)
		const expiresAtMs = Date.parse(data.expiresAt)

		// Already expired: nothing to ring up. An unreadable time is shown and left to the server's own refusal.
		if (!Number.isNaN(expiresAtMs) && expiresAtMs <= Date.now()) { return }
		deps.addReadyToast(data.inviteId, data.guestName, () => {
			clear(data.inviteId)
			deps.onRingUp(data.inviteId, data.guestName)
		})
		const notification = deps.notify(data.inviteId, data.guestName, () => {
			deps.focusApp()
		})

		notifications.set(data.inviteId, notification)
		if (!Number.isNaN(expiresAtMs)) { dropAtExpiry(data.inviteId, expiresAtMs) }
	}

	const onLeft = (data: { inviteId: string, guestName: string }) => {
		clear(data.inviteId)
		deps.addLeftToast(data.guestName)
	}

	const onLoggedOut = () => {
		for (const inviteId of new Set([...expiryTimers.keys(), ...notifications.keys()])) { clear(inviteId) }
	}

	globalEvents.on('GuestInvite:Ready', onReady)
	globalEvents.on('GuestInvite:Left', onLeft)
	globalEvents.on('Auth:LoggedOut', onLoggedOut)

	return {
		stop() {
			globalEvents.off('GuestInvite:Ready', onReady)
			globalEvents.off('GuestInvite:Left', onLeft)
			globalEvents.off('Auth:LoggedOut', onLoggedOut)

			for (const timer of expiryTimers.values()) { clearTimeout(timer) }
			expiryTimers.clear()
		}
	}
}
